// src/modules/notifications/stream.js
//
// The live channel: a thin wrapper over EventSource.
//
// This replaces a 157-line hand-rolled websocket client. EventSource already
// does reconnection, exponential-ish backoff and connection lifecycle, so all
// that survives here is what the browser does NOT do for us: carrying the JWT
// (EventSource cannot set headers, so it rides the query string), giving up on
// an auth failure instead of retrying a dead credential forever, and fanning
// one connection out to several subscribers.
//
// Nothing in the app depends on this being connected. The bell fetches its list
// over REST; the stream only tells it when to update sooner. If the transport
// is off (REALTIME_TRANSPORT=noop) or the browser cannot hold a stream, the app
// degrades to fetch-on-open and stays correct.

import API from '../../services/api';

// EventSource cannot report an HTTP status. What it can tell us is that a
// connection closed and reopened without ever delivering our `ready` event —
// repeat that enough times and the cause is almost always auth, not the
// network, so we stop rather than hammer the server with a dead token.
const MAX_FAILURES_BEFORE_GIVING_UP = 5;
const BACKOFF_BASE_MS = 2000;
const BACKOFF_MAX_MS = 60000;

class NotificationStream {
  constructor() {
    this.es = null;
    this.token = null;
    this.listeners = new Set();
    this.statusListeners = new Set();
    this.failures = 0;
    this.timer = null;
    this.status = 'idle'; // idle | connecting | live | degraded
    this.wantConnection = false;
  }

  _url() {
    const base = (API.defaults.baseURL || '').replace(/\/$/, '');
    return `${base}/notifications/stream?token=${encodeURIComponent(this.token)}`;
  }

  _setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((cb) => { try { cb(status); } catch (e) { /* listener's problem */ } });
  }

  connect(token) {
    if (!token) return;
    this.token = token;
    this.wantConnection = true;
    this.failures = 0;
    this._open();
  }

  _open() {
    if (!this.wantConnection || !this.token) return;
    if (typeof EventSource === 'undefined') { this._setStatus('degraded'); return; }
    if (this.es && this.es.readyState !== 2 /* CLOSED */) return;

    this._clearTimer();
    this._setStatus('connecting');

    let es;
    try {
      es = new EventSource(this._url());
    } catch (err) {
      this._setStatus('degraded');
      return;
    }
    this.es = es;

    // The server's first frame. Receiving it proves the token was accepted, so
    // it is what resets the failure counter — not `onopen`, which fires for a
    // connection the server is about to reject.
    es.addEventListener('ready', () => {
      this.failures = 0;
      this._setStatus('live');
    });

    ['notification.created', 'notification.read', 'notification.dismissed', 'notification.bulk',
      'permissions.changed', 'capture.files', 'kyc.digilocker', 'esign.updated'].forEach((event) => {
      es.addEventListener(event, (e) => this._emit(event, e));
    });

    es.onerror = () => {
      // Browsers reconnect on their own while readyState is CONNECTING; only
      // step in once EventSource has actually given up.
      if (es.readyState !== 2) return;
      this._teardown();
      this.failures += 1;

      if (this.failures >= MAX_FAILURES_BEFORE_GIVING_UP) {
        // Almost certainly an expired or rejected token. Stop; the app still
        // works over REST, and AuthContext calls connect() again after the next
        // successful authentication.
        this._setStatus('degraded');
        return;
      }

      const delay = Math.min(BACKOFF_BASE_MS * (2 ** (this.failures - 1)), BACKOFF_MAX_MS);
      this._setStatus('connecting');
      this.timer = setTimeout(() => this._open(), delay);
    };
  }

  _emit(event, e) {
    let payload = {};
    try { payload = e.data ? JSON.parse(e.data) : {}; } catch (err) { payload = {}; }
    this.listeners.forEach((cb) => {
      try { cb({ event, payload }); } catch (err) { console.warn('[notif-stream] listener threw', err); }
    });
  }

  _clearTimer() {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  }

  _teardown() {
    if (this.es) { try { this.es.close(); } catch (e) { /* already gone */ } }
    this.es = null;
  }

  /** Re-try after a giving-up, e.g. once the user has re-authenticated. */
  retry(token) {
    if (token) this.token = token;
    this.failures = 0;
    this.wantConnection = true;
    this._open();
  }

  disconnect() {
    this.wantConnection = false;
    this._clearTimer();
    this._teardown();
    this._setStatus('idle');
  }

  /** @returns {Function} unsubscribe */
  subscribe(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  /** @returns {Function} unsubscribe */
  onStatus(cb) {
    this.statusListeners.add(cb);
    cb(this.status);
    return () => this.statusListeners.delete(cb);
  }
}

const stream = new NotificationStream();
export default stream;
